import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Check, CheckCircle, Briefcase, FileText, User, FilePlus } from 'react-feather';
import logo from '../../../assets/logo.png';

// --- STYLED COMPONENTS ---

const PageContainer = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
  background-color: ${p => p.theme.colors.background.main};
`;

const SidebarContainer = styled.div`
  display: none;
  
  @media (min-width: 1024px) {
    display: flex;
    flex-direction: column;
    width: 320px;
    background-color: ${p => p.theme.colors.background.selected === '#f3f4f6' ? '#111827' : p.theme.colors.background.paper};
    color: #ffffff;
    padding: 40px 30px;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  }
`;

const SidebarLogo = styled.img`
  width: 120px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  margin-bottom: 60px;
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  position: relative;
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 15px; /* Center of icon */
    top: 40px;
    bottom: -20px;
    width: 2px;
    background-color: #22c55e;
    z-index: 1;
  }
`;

const StepIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #22c55e;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 2;
`;

const StepTextContent = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 4px;
`;

const StepLabel = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${p => p.theme.colors.text.primary};
`;

const StepDesc = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${p => p.theme.colors.text.secondary};
  margin-top: 4px;
`;

// --- CONTENT AREA ---

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: relative;
`;

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: ${p => p.theme.colors.background.paper};
  border-bottom: 1px solid ${p => p.theme.colors.border.main};
  position: sticky;
  top: 0;
  z-index: 10;
  
  @media (min-width: 1024px) {
    display: none;
  }
`;

const MobileLogo = styled.img`
  width: 120px;
  object-fit: contain;
  filter: ${p => p.theme.colors.background.paper === '#ffffff' ? 'none' : 'brightness(0) invert(1)'};
`;

const FormArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  padding: 40px 20px;
  
  @media (min-width: 1024px) {
    padding: 60px 40px;
  }
`;

const SuccessIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${p => p.theme.colors.background.selected === '#f3f4f6' ? '#f0fdf4' : 'rgba(34, 197, 94, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 4px 10px rgba(34, 197, 94, 0.2);
  
  svg {
    color: #22c55e;
  }
`;

const Title = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: ${p => p.theme.colors.text.primary};
  margin: 0 0 16px 0;
  text-align: center;
  letter-spacing: -0.5px;
`;

const MessageText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  color: ${p => p.theme.colors.text.secondary};
  line-height: 1.6;
  text-align: center;
  margin: 0 0 12px 0;
  max-width: 420px;
`;

const PrimaryButton = styled.button`
  background-color: ${p => p.theme.colors.text.primary};
  color: ${p => p.theme.colors.background.paper};
  border: none;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  padding: 16px 40px;
  border-radius: 10px;
  transition: all 0.2s;
  margin-top: 32px;
  
  &:hover {
    background-color: ${p => p.theme.colors.primary.light};
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

// --- DATA ---
const STEPS = [
  { id: 1, title: 'Entity Details', desc: 'Basic company information', icon: Briefcase },
  { id: 2, title: 'Operations', desc: 'Industry and volume', icon: FileText },
  { id: 3, title: 'Beneficial Owners', desc: 'Authorized representatives', icon: User },
  { id: 4, title: 'Documents', desc: 'Verification files', icon: FilePlus },
];

const OnboardingComplete = () => {
  const navigate = useNavigate();

  return (
    <PageContainer>
      {/* --- DESKTOP SIDEBAR --- */}
      <SidebarContainer>
        <SidebarLogo src={logo} alt="NYBANQ" />
        <StepList>
          {STEPS.map((step) => (
            <StepItem key={step.id} isCompleted={true}>
              <StepIconWrapper>
                <Check size={16} strokeWidth={3} />
              </StepIconWrapper>
              <StepTextContent>
                <StepLabel>{step.title}</StepLabel>
                <StepDesc>{step.desc}</StepDesc>
              </StepTextContent>
            </StepItem>
          ))}
        </StepList>
      </SidebarContainer>

      {/* --- CONTENT AREA --- */}
      <ContentContainer>
        <MobileHeader>
          <MobileLogo src={logo} alt="NYBANQ" />
        </MobileHeader>

        <FormArea>
          <SuccessIconWrapper>
            <CheckCircle size={40} />
          </SuccessIconWrapper>

          <Title>Application Submitted!</Title>
          <MessageText>
            We have successfully received your account opening application.
          </MessageText>
          <MessageText>
            Your information is currently under review by our compliance team. One of our executives will contact you shortly to follow up.
          </MessageText>

          <PrimaryButton onClick={() => navigate('/login')}>
            Return to Login
          </PrimaryButton>
        </FormArea>
      </ContentContainer>
    </PageContainer>
  );
};

export default OnboardingComplete;